import { z } from 'zod'

/**
 * The settings schema.
 *
 * One Zod schema per entity, shared between the form and any edge function that
 * touches the same shape. Percentages are handled as percentages here — 15, not
 * 0.15 — because that is what she types; the conversion to the fraction the
 * database stores happens at the single boundary below, not scattered through
 * the form.
 */

const optionalText = z
  .string()
  .trim()
  .transform((value) => (value === '' ? null : value))
  .nullable()

const requiredText = (label: string, max = 200) =>
  z.string().trim().min(1, `${label} is needed.`).max(max, `${label} is too long.`)

export const settingsSchema = z
  .object({
    // Identity
    business_name: requiredText('A business name'),
    legal_name: requiredText('A legal name'),
    registration_number: optionalText,
    public_email: z.string().trim().email('That is not a valid email address.'),
    reply_to_email: z.string().trim().email('That is not a valid email address.'),
    phone: optionalText,
    website_url: z.string().trim().url('That is not a valid URL.'),

    // Tax
    vat_registered: z.boolean(),
    vat_number: optionalText,
    vat_rate_pct: z.coerce
      .number({ message: 'Enter a number.' })
      .min(0, 'A VAT rate cannot be negative.')
      .max(99, 'A VAT rate that high is not a VAT rate.'),

    // Travel origin
    home_base_label: requiredText('A name for your home base'),
    home_base_address: requiredText('A full address', 400),
    home_base_lat: z.coerce
      .number({ message: 'Enter a number.' })
      .min(-90, 'Latitude runs from -90 to 90.')
      .max(90, 'Latitude runs from -90 to 90.'),
    home_base_lng: z.coerce
      .number({ message: 'Enter a number.' })
      .min(-180, 'Longitude runs from -180 to 180.')
      .max(180, 'Longitude runs from -180 to 180.'),

    // Quoting
    default_quote_validity_days: z.coerce
      .number({ message: 'Enter a number.' })
      .int('Whole days only.')
      .min(1, 'A quote has to be valid for at least a day.')
      .max(365, 'A year is the longest this allows.'),
    default_deposit_pct: z.coerce
      .number({ message: 'Enter a number.' })
      .min(0, 'A deposit cannot be negative.')
      .max(100, 'A deposit cannot exceed the total.'),
    default_balance_due_days_before_event: z.coerce
      .number({ message: 'Enter a number.' })
      .int('Whole days only.')
      .min(0, 'Enter zero or more days.')
      .max(365, 'A year is the longest this allows.'),
    quote_number_prefix: requiredText('A quote prefix', 8),
    invoice_number_prefix: requiredText('An invoice prefix', 8),

    // Delivery promises
    photo_delivery_weeks: z.coerce.number({ message: 'Enter a number.' }).int('Whole weeks only.').min(1, 'At least a week.').max(52, 'A year is the longest this allows.'),
    film_delivery_weeks: z.coerce.number({ message: 'Enter a number.' }).int('Whole weeks only.').min(1, 'At least a week.').max(52, 'A year is the longest this allows.'),

    // Payment reminders (sent by the email step)
    reminder_days_before: z.coerce.number({ message: 'Enter a number.' }).int('Whole days only.').min(0, 'Zero or more.').max(60, 'Sixty days at most.'),
    reminder_on_due_date: z.boolean(),
    reminder_overdue_every_days: z.coerce.number({ message: 'Enter a number.' }).int('Whole days only.').min(2, 'Every two days is the most often this allows.').max(60, 'Sixty days at most.'),
    reminder_overdue_max: z.coerce.number({ message: 'Enter a number.' }).int('Whole numbers only.').min(0, 'Zero or more.').max(10, 'Ten at most.'),

    // Banking
    bank_account_name: optionalText,
    bank_name: optionalText,
    bank_branch_code: optionalText,
    bank_account_number: optionalText,
    bank_account_type: optionalText,
  })
  .superRefine((values, ctx) => {
    // Mirrors the check constraint on the table, so the form catches it before
    // the database does and the message lands on the right field.
    if (values.vat_registered && !values.vat_number) {
      ctx.addIssue({
        code: 'custom',
        path: ['vat_number'],
        message: 'A VAT number is required once you are registered.',
      })
    }
  })

export type SettingsFormValues = z.input<typeof settingsSchema>
export type SettingsParsed = z.output<typeof settingsSchema>

/**
 * Form values to the row shape. The only place percentages become fractions.
 */
export function toRowUpdate(values: SettingsParsed) {
  const { vat_rate_pct, default_deposit_pct, ...rest } = values

  return {
    ...rest,
    vat_rate: vat_rate_pct / 100,
    default_deposit_pct: default_deposit_pct / 100,
  }
}
